/**
 * Rivals Strip — Hero strip placed at top of dashboard.
 * Shows current rank + 2 rivals above + 1 below for near-miss motivation.
 * Always visible, non-intrusive, single-row layout.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Flame, ChevronUp, ChevronDown, ArrowRight, Crown } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const RivalsStrip = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) { setLoading(false); return; }
      try {
        const res = await fetch(`${API_BASE}/api/leaderboard/rivals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok && mounted) setData(await res.json());
      } catch (e) {
        console.error('Rivals fetch failed', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div
        className="h-24 lg:h-28 rounded-2xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 animate-pulse"
        data-testid="rivals-strip-loading"
      />
    );
  }

  if (!data) return null;

  // Brand new student (no labs/mocks yet) → show onboarding nudge
  if (!data.you) {
    return (
      <Link
        to="/courses"
        className="block bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl p-4 shadow-lg hover:shadow-xl transition-shadow group"
        data-testid="rivals-strip-onboarding"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold">Complete your first lab to enter the rankings</p>
              <p className="text-emerald-50 text-sm">Champions are climbing — claim your spot.</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform" />
        </div>
      </Link>
    );
  }

  const targetAhead = data.above?.[0];  // immediate rival above
  const labsToTarget = targetAhead
    ? Math.max(0, targetAhead.labs_completed - data.you.labs_completed)
    : 0;
  const scoreGap = targetAhead
    ? Math.max(0, targetAhead.mastery_score - data.you.mastery_score)
    : 0;

  return (
    <Link
      to="/leaderboard"
      className="block bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 rounded-2xl shadow-xl hover:shadow-2xl transition-all group overflow-hidden"
      data-testid="rivals-strip"
    >
      <div className="relative p-4 lg:p-5">
        {/* Decorative blobs */}
        <div className="absolute -top-12 -right-12 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute -bottom-10 left-1/3 w-32 h-32 bg-amber-400/10 rounded-full blur-2xl pointer-events-none" />

        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Your Rank */}
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex flex-col items-center justify-center shadow-lg shrink-0">
              <Crown className="w-4 h-4 text-white" />
              <span className="text-white font-bold text-sm leading-none mt-0.5">#{data.you.rank}</span>
            </div>
            <div>
              <p className="text-white font-bold text-base lg:text-lg leading-tight">
                Your Rank
                <span className="text-amber-300 ml-1.5">#{data.you.rank}</span>
              </p>
              <div className="flex items-center gap-3 text-xs text-white/80 mt-0.5">
                <span className="flex items-center gap-1">
                  <Trophy className="w-3 h-3 text-amber-300" />
                  {data.you.labs_completed} labs
                </span>
                {data.you.mocks_passed > 0 && (
                  <span className="flex items-center gap-1">
                    🎯 {data.you.mocks_passed} mocks passed
                  </span>
                )}
                {data.you.streak_days > 0 && (
                  <span className="flex items-center gap-1 text-orange-300 font-semibold">
                    <Flame className="w-3 h-3" />
                    {data.you.streak_days}d streak
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Rival cluster (target above + threat below) */}
          <div className="flex items-center gap-2 flex-wrap">
            {targetAhead && (
              <div className="bg-white/10 backdrop-blur rounded-xl px-3 py-2 border border-white/20">
                <div className="flex items-center gap-2">
                  <ChevronUp className="w-4 h-4 text-emerald-300" />
                  <div>
                    <p className="text-[10px] text-white/60 uppercase tracking-wider leading-none">Catch up</p>
                    <p className="text-white text-sm font-semibold leading-tight">
                      {targetAhead.display_name}
                    </p>
                    <p className="text-amber-300 text-xs font-medium">
                      {labsToTarget > 0
                        ? `${labsToTarget} ${labsToTarget === 1 ? 'lab' : 'labs'} ahead`
                        : `${scoreGap} pts ahead`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {data.below && (
              <div className="bg-white/5 backdrop-blur rounded-xl px-3 py-2 border border-white/10">
                <div className="flex items-center gap-2">
                  <ChevronDown className="w-4 h-4 text-rose-300" />
                  <div>
                    <p className="text-[10px] text-white/60 uppercase tracking-wider leading-none">Stay ahead</p>
                    <p className="text-white text-sm font-semibold leading-tight">
                      {data.below.display_name}
                    </p>
                    <p className="text-rose-200 text-xs">
                      {Math.max(0, data.you.mastery_score - data.below.mastery_score)} pts behind
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-1 text-white/90 text-sm font-semibold pl-1 group-hover:text-amber-300 transition">
              View all
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
